<script>
  import { createEventDispatcher, onMount } from "svelte";
  import alertify from "alertifyjs";
  import backend from "../../../../../server";
  import Dropdown from "../../../ui/Dropdown.svelte";
  import InfoTooltip from "../../../ui/InfoTooltip.svelte";
  import {
    loadAnakatechClients,
    loadAnakatechGames,
  } from "./utils/anakatechCatalogs.js";
  import {
    generateAnakatechFreeRoundCode,
    parseLocalTimeToEpochMilliseconds,
  } from "./utils/anakatechFreeSpin.js";

  export let isNew = false;
  export let clientCode = "";
  export let clientId = "";
  export let saving = false;

  const dispatch = createEventDispatcher();
  let clients = [];
  let selectedClientId = clientId || "";
  let isLoadingClients = false;
  let games = [];
  let selectedGameId = "";
  let isLoadingGames = false;
  let baseBetValues = [];
  let gameBetInfo = null;
  let betOptionsLoading = false;
  let betOptionsError = "";
  let selectedCurrency = "";
  let currenciesError = "";
  let currencyMultiplier = null;
  let multiplierLoading = false;
  let multiplierError = "";
  let loadedGameId = null;
  let loadedCurrency = null;
  let loadedCurrencyClientCode = null;
  let loadedGamesKey = null;
  let betOptionsSequence = 0;
  let multiplierSequence = 0;
  let gamesRequestSequence = 0;
  let form = {
    assignationMode: "",
    betPerLine: "",
    expirationTime: "",
    startTime: "",
    numberOfRounds: "",
    freeRoundId: "",
    freeRoundName: "",
  };

  const ASSIGNATION_OPTIONS = [
    { value: "AUTO", description: "Automática" },
    { value: "MANUAL", description: "Manual" },
  ];

  $: selectedClient = clients.find(
    (client) => String(client?.id) === String(selectedClientId),
  );
  $: selectedClientCode = selectedClient?.code ?? "";
  $: betPerLineOptions = Number(currencyMultiplier) > 0
    ? baseBetValues.map((bet) => {
        const calculatedValue = bet * Number(currencyMultiplier);
        return {
          value: calculatedValue,
          description: formatMinorUnits(calculatedValue),
          baseBet: bet,
        };
      })
    : [];
  $: selectedBetOption = betPerLineOptions.find(
    (option) => Number(option.value) === Number(form.betPerLine),
  );
  $: convertedBetPerLine = selectedBetOption
    ? Number(selectedBetOption.value)
    : null;
  $: convertedBetAmount = convertedBetPerLine === null
    ? null
    : formatMinorUnits(convertedBetPerLine);
  $: numberOfLines = Number(gameBetInfo?.numberOfLines);
  $: hasValidNumberOfLines = Number.isInteger(numberOfLines) && numberOfLines >= 0;
  $: effectiveNumberOfLines = numberOfLines === 0 ? 1 : numberOfLines;
  $: betPerRound = convertedBetPerLine === null || !hasValidNumberOfLines
    ? null
    : convertedBetPerLine * effectiveNumberOfLines;
  $: betPerRoundAmount = betPerRound === null
    ? ""
    : formatMinorUnits(betPerRound);
  $: betOptionsKey = `${selectedClientCode}:${selectedGameId}`;
  $: if (betOptionsKey !== loadedGameId) {
    loadedGameId = betOptionsKey;
    loadBetOptions(selectedGameId, selectedClientCode);
  }
  $: if (selectedClientCode !== loadedCurrencyClientCode) {
    loadedCurrencyClientCode = selectedClientCode;
    setClientCurrency(selectedClient);
  }
  $: if (selectedCurrency !== loadedCurrency) {
    loadedCurrency = selectedCurrency;
    loadCurrencyMultiplier(selectedCurrency);
  }
  $: gamesKey = `${selectedClientCode}|${selectedCurrency}`;
  $: if (gamesKey !== loadedGamesKey) {
    loadedGamesKey = gamesKey;
    loadGames(selectedClientCode, selectedCurrency);
  }
  function responsePayload(response) {
    return response?.data ?? response ?? {};
  }

  function formatMinorUnits(value) {
    const amount = Number(value) / 100;
    return Number.isFinite(amount) ? String(Number(amount.toFixed(2))) : "";
  }

  function normalizeCurrency(value) {
    return String(value ?? "").trim().toUpperCase();
  }

  function setClientCurrency(client) {
    selectedCurrency = normalizeCurrency(client?.currency);
    currenciesError = client && !selectedCurrency
      ? "El cliente no tiene una moneda configurada"
      : "";
  }

  function generateFreeRoundCode() {
    if (!selectedClientCode) {
      alertify.alert("Selecciona un cliente", "Debes seleccionar un cliente antes de generar el código.");
      return;
    }
    form = {
      ...form,
      freeRoundId: generateAnakatechFreeRoundCode(selectedClientCode),
    };
  }

  function selectDefaultBet() {
    if (!(Number(currencyMultiplier) > 0) || !baseBetValues.length) {
      form = { ...form, betPerLine: "" };
      return;
    }

    const baseBet = baseBetValues.includes(gameBetInfo?.defaultBet)
      ? gameBetInfo.defaultBet
      : baseBetValues[0];
    form = {
      ...form,
      betPerLine: baseBet * Number(currencyMultiplier),
    };
  }

  async function loadBetOptions(gameId, clientCode) {
    const sequence = ++betOptionsSequence;
    baseBetValues = [];
    gameBetInfo = null;
    betOptionsError = "";
    form = { ...form, betPerLine: "" };
    if (!gameId || !clientCode) return;

    betOptionsLoading = true;
    try {
      const payload = responsePayload(
        await backend.freespin.getBetOptionsByGameIdAnakatech(
          String(gameId).trim(),
          clientCode,
        ),
      );
      if (sequence !== betOptionsSequence) return;
      if (payload?.success === false) {
        throw new Error(payload?.errorMessage || "No se encontraron opciones de apuesta");
      }
      const result = Array.isArray(payload?.result) ? payload.result : [];
      const info = result.find((item) => String(item?.gameId) === String(gameId)) ?? result[0];
      if (!info) throw new Error("El juego no tiene opciones de apuesta configuradas");

      baseBetValues = [...new Set(
        String(info?.bets ?? "")
          .split(",")
          .map((value) => Number(value.trim()))
          .filter((value) => Number.isFinite(value) && value > 0),
      )];
      gameBetInfo = {
        defaultBet: Number(info?.defaultBet),
        numberOfLines: Number(info?.numberOfLines),
      };
      if (!baseBetValues.length) throw new Error("El juego no devolvió valores de apuesta válidos");
      selectDefaultBet();
    } catch (error) {
      if (sequence !== betOptionsSequence) return;
      baseBetValues = [];
      gameBetInfo = null;
      form = { ...form, betPerLine: "" };
      betOptionsError = error?.response?.data?.errorMessage ?? error?.message ?? "No se pudieron cargar las apuestas";
    } finally {
      if (sequence === betOptionsSequence) betOptionsLoading = false;
    }
  }

  async function loadCurrencyMultiplier(currency) {
    const sequence = ++multiplierSequence;
    currencyMultiplier = null;
    multiplierError = "";
    form = { ...form, betPerLine: "" };
    if (!currency) return;

    multiplierLoading = true;
    try {
      const payload = responsePayload(
        await backend.freespin.getMultipliersPerIsoCodeAnakatech(currency),
      );
      if (sequence !== multiplierSequence) return;
      if (payload?.success === false) throw new Error("No se encontró el multiplicador de moneda");
      const currencies = Array.isArray(payload?.currencies) ? payload.currencies : [];
      const item = currencies.find(
        (entry) => String(entry?.isoCode ?? "").trim().toUpperCase() === currency,
      ) ?? currencies[0];
      const multiplier = Number(item?.multiplier);
      if (!(multiplier > 0)) throw new Error(`El multiplicador de ${currency} no es válido`);
      currencyMultiplier = multiplier;
      selectDefaultBet();
    } catch (error) {
      if (sequence !== multiplierSequence) return;
      currencyMultiplier = null;
      multiplierError = error?.response?.data?.error ?? error?.message ?? "No se pudo cargar el multiplicador";
    } finally {
      if (sequence === multiplierSequence) multiplierLoading = false;
    }
  }

  async function loadClients() {
    isLoadingClients = true;
    try {
      const catalog = await loadAnakatechClients();
      clients = catalog.clients;

      const initialClient = clients.find(
        (client) =>
          String(client?.id) === String(clientId) ||
          String(client?.code) === String(clientCode),
      );
      if (initialClient) selectedClientId = initialClient.id;
      else if (clients.length === 1) selectedClientId = clients[0].id;
    } catch (error) {
      console.error("Error cargando clientes:", error);
      clients = [];
      alertify.error("No se pudieron cargar los clientes");
    } finally {
      isLoadingClients = false;
    }
  }

  async function loadGames(clientCode, currency) {
    const sequence = ++gamesRequestSequence;
    games = [];
    if (!clientCode || !currency) {
      isLoadingGames = false;
      return;
    }
    isLoadingGames = true;
    try {
      const gameCatalog = await loadAnakatechGames(clientCode, currency);
      if (sequence !== gamesRequestSequence) return;
      games = [...gameCatalog].sort((a, b) =>
        a.gameName.localeCompare(b.gameName, "es", { sensitivity: "base" }),
      );
    } catch (error) {
      if (sequence !== gamesRequestSequence) return;
      console.error("Error cargando juegos de Anakatech:", error);
      games = [];
      alertify.error("No se pudieron cargar los juegos de Anakatech");
    } finally {
      if (sequence === gamesRequestSequence) isLoadingGames = false;
    }
  }

  function validate() {
    if (!selectedClientCode || !selectedClientId) {
      return "Selecciona un cliente";
    }
    if (!selectedCurrency) return "El cliente no tiene una moneda configurada";
    if (!["AUTO", "MANUAL"].includes(form.assignationMode)) {
      return "Selecciona un tipo de asignación";
    }
    if (!form.freeRoundId.trim()) return "Ingresa el código del Free Round";
    if (!form.freeRoundName.trim()) return "Ingresa el nombre de la campaña";
    if (!/^[A-Za-z0-9]+$/.test(form.freeRoundId.trim())) {
      return "El código del Free Round solo puede contener letras y números";
    }
    if (!selectedGameId) return "Selecciona un juego";
    if (!betPerLineOptions.some(
      (option) => Number(option.value) === Number(form.betPerLine),
    )) {
      return "Selecciona una apuesta por línea válida para el juego";
    }
    if (!(Number(currencyMultiplier) > 0)) {
      return "No se pudo obtener el multiplicador de la moneda seleccionada";
    }
    if (!hasValidNumberOfLines) {
      return "El juego no devolvió un número de líneas válido";
    }
    if (!(Number.isFinite(betPerRound) && betPerRound > 0)) {
      return "No se pudo calcular la apuesta por ronda";
    }

    const rounds = Number(form.numberOfRounds);
    if (!Number.isInteger(rounds) || rounds <= 0) {
      return "El número de rondas debe ser un entero mayor que cero";
    }

    const expirationTime = parseLocalTimeToEpochMilliseconds(form.expirationTime);
    if (!form.expirationTime || !Number.isFinite(expirationTime)) {
      return "Selecciona una fecha de expiración válida";
    }
    if (expirationTime <= Date.now()) {
      return "La fecha de expiración debe ser futura";
    }

    return "";
  }

  function save() {
    if (!form.freeRoundId.trim() && selectedClientCode) {
      form = {
        ...form,
        freeRoundId: generateAnakatechFreeRoundCode(selectedClientCode),
      };
    }
    const validationError = validate();
    if (validationError) {
      alertify.alert("Revisa los datos", validationError);
      return;
    }

    const campaignCode = form.freeRoundId.trim();
    const campaignName = form.freeRoundName.trim();
    const selectedGame = games.find(
      (game) => String(game?.gameId) === String(selectedGameId),
    );
    dispatch("save", {
      clientCode: selectedClientCode,
      currency: selectedCurrency,
      request: {
        assignationMode: form.assignationMode,
        betPerLine: convertedBetPerLine,
        betPerRound,
        brandId: null,
        customerId: null,
        description: campaignName + " - " +campaignName,
        expirationTime: parseLocalTimeToEpochMilliseconds(form.expirationTime),
        startTime: parseLocalTimeToEpochMilliseconds(form.startTime),
        gameId: String(selectedGameId),
        gameName: selectedGame?.gameName ?? String(selectedGameId),
        name: campaignName,
        numberOfRounds: Number(form.numberOfRounds),
        freeRoundId: campaignCode,
        playerIds: [],
        playerList: []
      },
    });
  }

  onMount(async () => {
    await Promise.all([loadClients()]);
  });
</script>

{#if isNew}
  <div class="modal-backdrop fade show"></div>
  <div
    class="modal d-block"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
    aria-labelledby="anakatech-free-spin-title"
  >
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 id="anakatech-free-spin-title" class="modal-title">
            Crear Free Spin Anakatech
          </h5>
          <button
            type="button"
            class="btn-close"
            aria-label="Cerrar"
            disabled={saving}
            on:click={() => dispatch("close")}
          ></button>
        </div>

        <div class="modal-body">
          <div class="sections-layout">
            <section aria-labelledby="anakatech-general-title">
              <h6 id="anakatech-general-title" class="section-title">General</h6>
              <div class="section-box form-grid general-grid">
                <div>
                  <span class="field-label">Cliente <span class="required">*</span></span>
                  <Dropdown
                    options={clients}
                    bind:value={selectedClientId}
                    valueKey="id"
                    labelKey="namesite"
                    isStructuredDescription={true}
                    paramStructuredName={["code", "namesite"]}
                    placeholder={isLoadingClients ? "Cargando clientes..." : "Seleccionar cliente..."}
                    disabled={saving || isLoadingClients}
                  />
                </div>

                <div>
                  <span class="field-label">Tipo de asignación <span class="required">*</span></span>
                  <Dropdown
                    options={ASSIGNATION_OPTIONS}
                    bind:value={form.assignationMode}
                    valueKey="value"
                    labelKey="description"
                    clearable={false}
                    placeholder="Seleccionar tipo..."
                    disabled={saving}
                  />
                </div>

                <label>
                  Moneda del cliente <span class="required">*</span>
                  <input
                    class="form-control"
                    value={selectedCurrency}
                    placeholder="—"
                    readonly
                    aria-readonly="true"
                  />
                  {#if currenciesError}
                    <small class="field-error">{currenciesError}</small>
                  {/if}
                </label>
              </div>
            </section>

            <section aria-labelledby="anakatech-campaign-details-title">
              <h6 id="anakatech-campaign-details-title" class="section-title">Detalles de campaña</h6>
              <div class="section-box form-grid campaign-grid">
                <div>
                  <span class="field-label">
                    Código de la campaña
                    <InfoTooltip info={["Opcional. Si no ingresas un código, el sistema generará uno al crear."]} />
                  </span>
                  <input
                    class="form-control"
                    bind:value={form.freeRoundId}
                    pattern="[A-Za-z0-9]+"
                    title="Solo se permiten letras y números"
                    disabled={saving}
                  />
                  <button type="button" class="generate-code-action" disabled={saving} on:click={generateFreeRoundCode}>
                    Generar código
                  </button>
                </div>

                <div>
                  <span class="field-label">
                    Nombre de la campaña
                  </span>
                  <input
                          class="form-control"
                          bind:value={form.freeRoundName}
                          disabled={saving}
                  />
                </div>

                <label>
                  Número de rondas <span class="required">*</span>
                  <input
                          type="number"
                          min="1"
                          step="1"
                          class="form-control"
                          bind:value={form.numberOfRounds}
                          disabled={saving}
                  />
                </label>

                <label>
                  Fecha de inicio <span class="required">*</span>
                  <input
                          type="datetime-local"
                          class="form-control"
                          bind:value={form.startTime}
                          disabled={saving}
                  />
                </label>

                <label>
                  Fecha de expiración <span class="required">*</span>
                  <input
                    type="datetime-local"
                    class="form-control"
                    bind:value={form.expirationTime}
                    disabled={saving}
                  />
                </label>

              </div>
            </section>

            <section aria-labelledby="anakatech-game-details-title">
              <h6 id="anakatech-game-details-title" class="section-title">Juegos</h6>
              <div class="section-box form-grid game-grid">
                <div>
                  <span class="field-label">Juego <span class="required">*</span></span>
                  <Dropdown
                    options={games}
                    bind:value={selectedGameId}
                    valueKey="gameId"
                    labelKey="gameName"
                    placeholder={isLoadingGames ? "Cargando juegos..." : "Seleccionar juego..."}
                    disabled={saving || isLoadingGames}
                  />
                </div>

                <div>
                  <span class="field-label">
                    Apuesta por línea <span class="required">*</span>
                    {#if convertedBetPerLine !== null}
                      <InfoTooltip
                        info={[`Apuesta por linea utiliza una equivalencia basada en EUROS. El multiplicador para ${selectedCurrency} es x${currencyMultiplier}`]}
                        ariaLabel="Equivalencia de la apuesta por línea"
                      />
                    {/if}
                  </span>
                  <Dropdown
                    options={betPerLineOptions}
                    bind:value={form.betPerLine}
                    clearable={false}
                    placeholder={betOptionsLoading ? "Cargando apuestas..." : "Seleccionar apuesta..."}
                    disabled={saving || betOptionsLoading || !betPerLineOptions.length}
                  />
                  {#if betOptionsError}
                    <small class="field-error">{betOptionsError}</small>
                  {:else if multiplierError}
                    <small class="field-error">{multiplierError}</small>
                  {/if}
                </div>

                <label>
                  Apuesta por ronda
                  <input
                    type="text"
                    class="form-control"
                    value={betPerRoundAmount ? `${betPerRoundAmount} ${selectedCurrency}` : ""}
                    placeholder="—"
                    readonly
                    aria-readonly="true"
                  />
                  {#if convertedBetPerLine !== null}
                    {#if numberOfLines === 0}
                      <small class="field-help">
                        El juego no define líneas; se usa la apuesta seleccionada como apuesta total de la ronda.
                      </small>
                    {:else}
                      <small class="field-help">
                        Apuesta por línea × número de líneas.
                      </small>
                    {/if}
                  {/if}
                </label>

                <label>
                  Número de líneas
                  <input
                    type="number"
                    class="form-control"
                    value={gameBetInfo?.numberOfLines ?? ""}
                    placeholder={betOptionsLoading ? "Cargando..." : "—"}
                    readonly
                    aria-readonly="true"
                  />
                  <small class="field-help">Dato informativo del juego.</small>
                </label>
              </div>
            </section>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            disabled={saving}
            on:click={() => dispatch("close")}
          >
            Cancelar
          </button>
          <button
            type="button"
            class="btn btn-primary"
            disabled={saving || isLoadingGames || betOptionsLoading || multiplierLoading}
            on:click={save}
          >
            {saving ? "Creando..." : "Crear Free Spin"}
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal {
    background: rgba(0, 0, 0, 0.3);
  }

  .sections-layout {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
  }

  .section-title {
    margin: 0 0 0.5rem;
    color: #344054;
    font-size: 0.95rem;
    font-weight: 650;
  }

  .section-box {
    padding: 0.75rem;
    border: 1px solid #e2e6ea;
    border-radius: 0.75rem;
    background: #fff;
  }

  .form-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1rem;
  }

  label,
  .field-label {
    color: #444;
    font-size: 0.875rem;
    font-weight: 600;
  }

  label input {
    margin-top: 0.35rem;
  }

  .field-label {
    display: block;
    margin-bottom: 0.35rem;
  }

  .required {
    color: #dc3545;
  }

  .field-help,
  .field-error {
    display: block;
    margin-top: 0.3rem;
    font-size: 0.75rem;
    font-weight: 400;
  }

  .field-help {
    color: #6c757d;
  }

  .field-error {
    color: #dc3545;
  }

  .generate-code-action {
    display: block;
    margin-top: 0.3rem;
    padding: 0;
    border: 0;
    background: transparent;
    color: #0b5ed7;
    font-size: 0.75rem;
    font-weight: 600;
    text-align: left;
    cursor: pointer;
  }

  .generate-code-action:hover:not(:disabled) {
    color: #084298;
    text-decoration: underline;
  }

  .generate-code-action:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }


  @media (max-width: 900px) {
    .form-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 700px) {
    .form-grid {
      grid-template-columns: minmax(0, 1fr);
    }

  }
</style>
